import React from 'react';
import { motion } from 'framer-motion';
import {
  Eye, Edit3, FilePlus, FileX, FolderSearch, Search, Terminal,
  ChevronRight,
} from 'lucide-react';
import type { UICard, Verb } from './types';

function fileIcon(verb: Verb) {
  switch (verb) {
    case 'viewed':    return Eye;
    case 'edited':    return Edit3;
    case 'created':   return FilePlus;
    case 'deleted':   return FileX;
    case 'listed':    return FolderSearch;
    case 'searched':  return Search;
    case 'retrieved': return Search;
    case 'ran':       return Terminal;
  }
}

function verbColor(verb: Verb): string {
  switch (verb) {
    case 'created': return 'text-emerald-400';
    case 'deleted': return 'text-rose-400';
    case 'edited':  return 'text-violet-300';
    case 'ran':     return 'text-cyan-300';
    default:        return 'text-violet-300';
  }
}

function verbLabel(verb: Verb): string {
  const m: Record<Verb, string> = {
    viewed: 'Viewed', edited: 'Edited', created: 'Created',
    deleted: 'Deleted', listed: 'Listed', ran: 'Ran',
    searched: 'Searched', retrieved: 'Retrieved',
  };
  return m[verb];
}

export const FileCard: React.FC<{
  card: UICard; onOpen: () => void;
}> = ({ card, onOpen }) => {
  const Icon = fileIcon(card.verb);
  const label = card.path ? card.path.split('/').pop() : card.label;
  return (
    <motion.button
      type="button"
      onClick={onOpen}
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      className="group w-full max-w-[420px] flex items-center gap-3 bg-white/[0.03] hover:bg-white/[0.06] active:bg-white/[0.10] border border-white/[0.06] hover:border-violet-400/30 rounded-2xl px-3 py-2.5 transition-all text-left touch-manipulation"
      data-testid={`chat-card-${card.verb}`}
    >
      <div className={`w-9 h-9 rounded-xl bg-violet-500/10 border border-violet-400/20 grid place-items-center shrink-0 ${verbColor(card.verb)}`}>
        <Icon className="w-4 h-4" strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">
          {verbLabel(card.verb)}
        </div>
        <div className="text-sm text-zinc-100 font-mono truncate">
          {label}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-violet-300 transition-colors shrink-0" />
    </motion.button>
  );
};
